/// <reference types="@types/google.maps" />
// declare module 'googlemaps';

import {
  EnvironmentInjector,
  ErrorHandler,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { traceUntilFirst } from '@angular/fire/performance';
import { GoogleMapsServiceInterface, PlaceResult } from '@vmspdb/contracts';
import { AddressComponentsJson, convertGeocode } from '@vmspdb/definitions';
import { filter, from, map, Observable, of } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService implements GoogleMapsServiceInterface {
  private readonly apiLoaded$: Observable<boolean>;
  private autocompleteSuggestion:
    typeof google.maps.places.AutocompleteSuggestion | null = null;
  private geocoder: google.maps.Geocoder | null = null;
  private readonly clusterScriptLoad$: Observable<boolean>;
  private readonly injector = inject(EnvironmentInjector);

  constructor() {
    const fb = inject(FirebaseApp);
    const errorHandler = inject(ErrorHandler);

    setOptions({
      key: fb.options.apiKey ?? '',
      v: 'weekly',
      libraries: ['maps', 'marker', 'places'],
    });

    this.apiLoaded$ = from(importLibrary('places')).pipe(
      traceUntilFirst(`maps load`),
      tap((lib) => (this.autocompleteSuggestion = lib.AutocompleteSuggestion)),
      tap(() => (this.geocoder = new google.maps.Geocoder())),
      map(() => true),
      catchError((err) => {
        errorHandler.handleError(err);
        return of(false);
      }),
      shareReplay(),
    );
    this.clusterScriptLoad$ = this.apiLoaded$.pipe(
      filter((loaded) => loaded),
      map(() => {
        return true;
      }),
      shareReplay(),
    );
  }

  get loaded$() {
    return this.apiLoaded$;
  }

  get clusterLoaded$() {
    return this.clusterScriptLoad$;
  }

  autocomplete(
    address: string,
  ): Observable<google.maps.places.PlacePrediction[]> {
    if (this.autocompleteSuggestion === null) {
      return this.loaded$.pipe(
        switchMap(() => of<google.maps.places.PlacePrediction[]>([])),
      );
    }

    const result$ = from(
      this.autocompleteSuggestion.fetchAutocompleteSuggestions({
        input: address,
        includedRegionCodes: ['us'],
        includedPrimaryTypes: [
          'street_address',
          'route',
          'premise',
          'subpremise',
        ],
      }),
    ).pipe(
      map(({ suggestions }) =>
        suggestions
          .map((suggestion) => suggestion.placePrediction)
          .filter(
            (
              placePrediction,
            ): placePrediction is google.maps.places.PlacePrediction =>
              placePrediction !== null,
          ),
      ),
    );

    // traceUntilFirst() injects when it is called, and autocomplete() is not
    // called from an injection context.
    const trace = runInInjectionContext(this.injector, () =>
      traceUntilFirst<boolean>(`maps places/autocomplete`),
    );

    return this.loaded$.pipe(
      trace,
      switchMap(() => result$),
    );
  }

  getPlace(place: google.maps.places.PlacePrediction): Observable<PlaceResult> {
    return this.geocode({
      placeId: place.placeId,
    });
  }

  geocode(request: google.maps.GeocoderRequest): Observable<PlaceResult> {
    const GeocoderStatus = google.maps.GeocoderStatus;

    const result$ = new Observable<PlaceResult>((subscriber) => {
      this.geocoder?.geocode(request, (results, status) => {
        if (!(
          status === GeocoderStatus.OK || status === GeocoderStatus.ZERO_RESULTS
        )) {
          subscriber.error(status);
          return;
        }
        subscriber.next({
          fullText: results?.length ? results[0].formatted_address : '',
          components: results?.map(convertGeocodeFrontend) ?? [],
        });
      });
    });

    const trace = runInInjectionContext(this.injector, () =>
      traceUntilFirst<boolean>(`maps geocoder/geocode`),
    );

    return this.loaded$.pipe(
      trace,
      switchMap(() => result$),
    );
  }
}

export function convertGeocodeFrontend(
  geo: google.maps.GeocoderResult,
): AddressComponentsJson {
  const lat = geo.geometry?.location?.lat(),
    lng = geo.geometry?.location?.lng(),
    placeId = geo.place_id;

  const result = convertGeocode(geo.address_components);

  if (lat !== undefined) {
    result.lat = lat;
  }

  if (lng !== undefined) {
    result.lng = lng;
  }

  if (placeId !== undefined) {
    result.placeId = placeId;
  }

  return result;
}
